import { useState } from "react";
import classNames from "classnames";

import { useTypedSelector } from "../../../../../../store/reducers/use-typed-selector";
import { Session, SessionTabsLayoutTypes } from "../../../../../../types/working-model";
import Icon, { COLORS, ICONS } from "../../../../../general-ui/icon";
import CollapsingList from "../../../../../general-ui/collapsing-list/collapsing-list";
import { SwitchNew } from "../../../../../general-ui/switch/switch-new";
import { batch, useDispatch } from "react-redux";
import { updateSession, updateWorkingSession } from "../../../../../../store/actions/admin/create-event/session";
import { updateAllEventSessionsLayoutTabs, updateWorkingEventSession } from "../../../../../../store/actions/admin/create-event";
import { UpdateAllSessionsLayoutTabs } from "../../../../../../connection/sessions";
import { addChecklistItem } from "../../../../../../store/actions/authentication";
import { ChecklistTitles } from "../../../../../../store/reducers/admin/assistant";

interface ITabsLayoutProps {
	handleClose: () => void;
}

const TabsLayout: React.FC<ITabsLayoutProps> = ({ handleClose }) => {

	const { workingSession, token, eventUuid, user } = useTypedSelector(state => ({
		workingSession: state.CreateSessionReducer.workingSession,
		eventUuid: state.CreateEventReducer.workingEvent?.uuid,
		token: state.AuthReducer.token,
		user: state.AuthReducer.user,
	}));

	const dispatch = useDispatch();

	const initLayout = workingSession?.layout?.tabs?.layout || SessionTabsLayoutTypes.Horizontal;
	const initShowIcons = workingSession?.layout?.tabs ? !!workingSession?.layout?.tabs?.show_icons : true;

	const [selectedLayout, setSelectedLayout] = useState<SessionTabsLayoutTypes>(initLayout);
	const [selectShowIcons, setSelectShowIcons] = useState(initShowIcons);
	const [applyToAllSessions, setApplyToAllSessions] = useState(false);

	const handleSave = async () => {
		if (!workingSession || !token || !eventUuid) return;
		try {
			const updatedLayoutTabs = {
				layout: selectedLayout,
				show_icons: selectShowIcons,
			};
			// update workingSession layout
			const updatedSession: Session = {
				...workingSession,
				layout: {
					...workingSession.layout,
					tabs: updatedLayoutTabs,
				},
			};
			// update database session and reducer working session:
			batch(() => {
				dispatch(updateSession(updatedSession, token));
				dispatch(updateWorkingSession(updatedSession));
			});
			if (applyToAllSessions) {
				// update all sessions in database:
				await UpdateAllSessionsLayoutTabs(token, eventUuid, updatedLayoutTabs);
				// update all sessions in workingEvent reducer
				dispatch(updateAllEventSessionsLayoutTabs(updatedLayoutTabs));
			} else {
				// update single session in workingEvent reducer
				dispatch(updateWorkingEventSession(updatedSession));
			}

			// ASSISTANT CHECKLIST ITEM: complete users checklist item for switching to vertical tab layout
			if (
				user &&
				updatedLayoutTabs.layout === SessionTabsLayoutTypes.Vertical &&
				!user.onboarded.includes(ChecklistTitles.customizeNavigation)
			) {
				dispatch(addChecklistItem(token!, user.id, user.onboarded, ChecklistTitles.customizeNavigation));
			}

			handleClose();
		} catch (e) {
			console.error(e?.response || e);
		}
	};

	const renderButton = (icon: string, name: SessionTabsLayoutTypes) => (
		<button
			className={classNames(
				'clear no-style inherit-dimensions layout-tabs-button',
				{ active: selectedLayout === name }
			)}
			onClick={() => setSelectedLayout(name)}
		>
			<div>
				<Icon name={icon} color={COLORS.DEFAULT} size={100} />
			</div>
			<div className="layout-tabs-button-text">
				{name}
			</div>
		</button>
	);

	return (
		<div>
			<div className="layout-tabs-buttons-container">
				{renderButton(ICONS.LAYOUT_NO_TABS, SessionTabsLayoutTypes.NoTabs)}
				{renderButton(ICONS.LAYOUT_HORIZONTAL, SessionTabsLayoutTypes.Horizontal)}
				{renderButton(ICONS.LAYOUT_VERTICAL, SessionTabsLayoutTypes.Vertical)}
			</div>
			<div className="advanced-settings-wrapper">
				<CollapsingList
					autoWidthTrigger
					label="Advanced settings"
					reverseIconPosition
				>
					<div style={{ marginTop: '16px' }}>
						<SwitchNew
							content={"Show icons"}
							checked={selectShowIcons}
							onChange={() => setSelectShowIcons(prev => !prev)}
						/>
					</div>
				</CollapsingList>
			</div>
			<div className="layout-tabs-footer">
				<div>
					<SwitchNew
						content="Apply to all sessions"
						onChange={(_, checked) => setApplyToAllSessions(checked)}
					/>
				</div>
				<div className="layout-tabs-footer-buttons">
					<button onClick={handleClose}>Cancel</button>
					<button onClick={handleSave} className="primary">Save</button>
				</div>
			</div>
		</div>
	);
};

export default TabsLayout;
